import { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Search, Split, AlertTriangle, Tag } from 'lucide-react';
import { marginCorrectionsApi } from '../../services/marginCorrectionsApi';
import { useToast } from '../ui/Toast';
import { useCorrectionDraft, toEur } from '../../hooks/useCorrectionDraft';
import type { InvoiceLineRow, CounterCandidate } from '../../types/marginCorrections';

const fmt = (n: number, d = 2) => n.toLocaleString('de-CH', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtCents = (c: number) => fmt(toEur(c));
const REASON_MIN = 5;

interface Props {
    company: string;
    source: InvoiceLineRow;
    dateFrom: string | null;
    onClose: () => void;
    onSaved: () => void;
}

/**
 * Right-click "Correct cost…" modal. Left column = the source line with an old→new margin preview;
 * right column = the counter-candidate picker. A balance bar at the bottom shows the residual;
 * Save is disabled until residual === 0 (in cents) AND the reason is long enough.
 */
export function CorrectCostModal({ company, source, dateFrom, onClose, onSaved }: Props) {
    const { toast } = useToast();
    const draft = useCorrectionDraft();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    // Candidate picker
    const [candidates, setCandidates] = useState<CounterCandidate[]>([]);
    const [candLoading, setCandLoading] = useState(false);
    const [onlySpecial, setOnlySpecial] = useState(false);
    const [specialAvailable, setSpecialAvailable] = useState(true);
    const [search, setSearch] = useState('');

    // Raw text of each line's delta input, keyed by documentID. Kept separate from the reducer's
    // cent value so the user can type intermediate states — a lone "-", a trailing "." — that
    // don't parse to a number yet. Counter-lines are negative, so typing "-" MUST survive.
    const [rawDelta, setRawDelta] = useState<Record<string, string>>({});
    const commitDelta = (documentID: string, raw: string) => {
        setRawDelta(prev => ({ ...prev, [documentID]: raw }));
        const trimmed = raw.trim();
        if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
            draft.setDeltaEur(documentID, 0);
            return;
        }
        const n = Number(trimmed);
        if (Number.isFinite(n)) draft.setDeltaEur(documentID, n);
    };
    // After "Split remaining", the reducer changed deltas the user didn't type — surface those.
    const deltaDisplay = (documentID: string, deltaCents: number) =>
        rawDelta[documentID] ?? (deltaCents === 0 ? '' : String(toEur(deltaCents)));

    // Seed the draft from the clicked line, once.
    useEffect(() => { draft.init(source); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [source.documentID]);

    const loadCandidates = useCallback(async () => {
        setCandLoading(true);
        try {
            const res = await marginCorrectionsApi.getCounterCandidates({
                company,
                excludeDocumentID: source.documentID,
                onlySpecial,
                search: search.trim() || undefined,
                dateFrom: dateFrom || undefined,
                limit: 100,
            });
            setCandidates(res.rows);
            setSpecialAvailable(res.specialLotsAvailable);
            // No special lots marked yet? The filter is inert — turn it off so the list isn't empty.
            if (onlySpecial && !res.specialLotsAvailable) setOnlySpecial(false);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load candidates', 'error');
        } finally {
            setCandLoading(false);
        }
    }, [company, source.documentID, onlySpecial, search, dateFrom, toast]);

    useEffect(() => {
        const t = setTimeout(loadCandidates, 250);
        return () => clearTimeout(t);
    }, [loadCandidates]);

    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose, saving]);

    const reasonOk = reason.trim().length >= REASON_MIN;
    const canSave = draft.isSubmittable && reasonOk && !saving;
    const inDraft = new Set(draft.lines.map(l => l.documentID));

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        try {
            const res = await marginCorrectionsApi.createGroup({
                company,
                reason: reason.trim(),
                lines: draft.lines.map(l => ({
                    targetDocumentID: l.documentID,
                    deltaEUR: toEur(l.deltaCents),
                    lineNote: l.lineNote.trim() || null,
                })),
            });
            if (res.warnings.length > 0) {
                toast(res.warnings[0].message, 'info');
            }
            toast(`Correction #${res.groupId} saved`, 'success');
            onSaved();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to save correction', 'error');
            setSaving(false);
        }
    };

    const previewMargin = (netCents: number, gpCents: number) => (netCents !== 0 ? (gpCents / netCents) * 100 : null);

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b">
                    <h2 className="text-lg font-semibold text-gray-900">Correct cost &amp; re-book</h2>
                    <button onClick={() => !saving && onClose()} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>

                {/* Two columns */}
                <div className="flex-1 overflow-hidden grid grid-cols-2 divide-x">
                    {/* LEFT — the correction lines with preview */}
                    <div className="overflow-y-auto p-4 space-y-3">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Correction lines</h3>
                        {draft.lines.map(l => {
                            const newGp = l.baseGrossProfitCents + l.deltaCents;
                            const oldM = previewMargin(l.netSalesCents, l.baseGrossProfitCents);
                            const newM = previewMargin(l.netSalesCents, newGp);
                            const goesNegative = draft.wouldGoNegative.some(w => w.documentID === l.documentID);
                            return (
                                <div key={l.documentID} className={`border rounded-lg p-3 ${l.isSource ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-mono text-sm font-medium">{l.documentCode}</span>
                                                {l.isSource && <span className="text-[10px] px-1.5 py-0.5 bg-blue-600 text-white rounded">SOURCE</span>}
                                            </div>
                                            <p className="text-xs text-gray-500 truncate max-w-[240px]" title={l.customerName}>{l.customerName}</p>
                                        </div>
                                        {!l.isSource && (
                                            <button onClick={() => draft.removeLine(l.documentID)} className="text-gray-300 hover:text-red-500 p-0.5"><Trash2 className="w-4 h-4" /></button>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 mb-2">
                                        <label className="text-xs text-gray-500 w-24">GP delta (EUR)</label>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={deltaDisplay(l.documentID, l.deltaCents)}
                                            onChange={e => commitDelta(l.documentID, e.target.value)}
                                            placeholder="0.00"
                                            className={`flex-1 border rounded px-2 py-1 text-sm text-right font-mono ${goesNegative ? 'border-red-400 bg-red-50' : ''}`}
                                        />
                                    </div>

                                    {/* old → new margin preview */}
                                    <div className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 border">
                                        <span className="text-gray-500">Margin</span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="text-gray-400">{oldM != null ? `${fmt(oldM, 1)}%` : '–'}</span>
                                            <span className="text-gray-300">→</span>
                                            <span className={`font-semibold ${newM != null && newM < 0 ? 'text-red-600' : newM != null && newM < 5 ? 'text-amber-600' : 'text-green-600'}`}>
                                                {newM != null ? `${fmt(newM, 1)}%` : '–'}
                                            </span>
                                        </span>
                                    </div>
                                    {goesNegative && (
                                        <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Resulting cost would be negative.</p>
                                    )}
                                    {!l.isSource && (
                                        <input
                                            type="text"
                                            value={l.lineNote}
                                            onChange={e => draft.setNote(l.documentID, e.target.value)}
                                            placeholder="Note (optional)"
                                            className="w-full border rounded px-2 py-1 text-xs mt-2"
                                            maxLength={500}
                                        />
                                    )}
                                </div>
                            );
                        })}

                        {draft.counters.length > 0 && (
                            <button onClick={() => { draft.splitRemaining(); setRawDelta({}); }} className="w-full flex items-center justify-center gap-1.5 text-xs text-blue-600 border border-blue-200 rounded-md py-1.5 hover:bg-blue-50">
                                <Split className="w-3.5 h-3.5" /> Split remaining evenly across counter-lines
                            </button>
                        )}
                    </div>

                    {/* RIGHT — candidate picker */}
                    <div className="overflow-hidden flex flex-col">
                        <div className="p-4 pb-2 space-y-2">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Counter-line candidates</h3>
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search document, customer, product…" className="w-full border rounded-md pl-8 pr-3 py-1.5 text-sm" />
                            </div>
                            <label className={`flex items-center gap-1.5 text-xs ${specialAvailable ? 'text-gray-600' : 'text-gray-400'}`}>
                                <input type="checkbox" checked={onlySpecial} disabled={!specialAvailable} onChange={e => setOnlySpecial(e.target.checked)} className="rounded" />
                                <Tag className="w-3.5 h-3.5" /> Special-price lots only
                                {!specialAvailable && <span className="italic">(none marked yet)</span>}
                            </label>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1.5">
                            {candLoading && <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" /></div>}
                            {!candLoading && candidates.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No candidates found</p>}
                            {!candLoading && candidates.map(c => {
                                const added = inDraft.has(c.documentID);
                                return (
                                    <button
                                        key={c.documentID}
                                        onClick={() => !added && draft.addCounter(c)}
                                        disabled={added}
                                        className={`w-full text-left border rounded-lg px-3 py-2 flex items-center justify-between ${added ? 'opacity-40 cursor-default bg-gray-50' : 'hover:border-blue-300 hover:bg-blue-50'}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-mono text-sm">{c.documentCode}</span>
                                                {c.isSpecialLot && <Tag className="w-3 h-3 text-purple-600" />}
                                                {c.isCorrected && <span className="text-[10px] text-blue-600">✎</span>}
                                            </div>
                                            <p className="text-xs text-gray-500 truncate max-w-[220px]" title={c.customerName}>{c.customerName}</p>
                                        </div>
                                        <div className="text-right shrink-0 ml-2">
                                            <div className="text-xs text-gray-600">{fmt(c.netSalesEUR, 0)} EUR</div>
                                            <div className={`text-xs ${c.marginPct != null && c.marginPct < 5 ? 'text-amber-600' : 'text-gray-400'}`}>{c.marginPct != null ? `${fmt(c.marginPct, 1)}%` : '–'}</div>
                                        </div>
                                        {!added && <Plus className="w-4 h-4 text-blue-500 ml-2 shrink-0" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Balance bar + reason + actions */}
                <div className="border-t p-4 space-y-3 bg-gray-50">
                    <div className={`flex items-center justify-between px-4 py-2.5 rounded-md text-sm font-medium ${draft.isBalanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        <span>{draft.isBalanced ? 'Balanced' : 'Residual — must reach 0.00 to save'}</span>
                        <span className="font-mono text-base">{draft.residualCents > 0 ? '+' : ''}{fmtCents(draft.residualCents)} EUR</span>
                    </div>

                    <div className="flex items-start gap-3">
                        <div className="flex-1">
                            <textarea
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                placeholder="Reason for this correction (required, min 5 characters)…"
                                rows={2}
                                maxLength={1000}
                                className="w-full border rounded-md px-3 py-2 text-sm resize-none"
                            />
                            {reason.length > 0 && !reasonOk && <p className="text-xs text-red-500 mt-0.5">At least {REASON_MIN} characters.</p>}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <button onClick={handleSave} disabled={!canSave} className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                                {saving ? 'Saving…' : 'Save correction'}
                            </button>
                            <button onClick={() => !saving && onClose()} className="px-5 py-1.5 text-sm text-gray-600 border rounded-md hover:bg-white">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
